import React, { useEffect, useMemo } from "react";
import { Inbox, InboxProps } from "@novu/react";
import { Badge, Box } from "@mui/material";
import { useTheme } from "@mui/joy";
import { NotificationsFilledRounded } from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useNovuSubscriber } from "../NotificationsProvider";
import { useReportFileDownload } from "../../hooks/useReportFileDownload";
import { parseInstalledBaseFileNotificationPath } from "../../utils/novuNotificationPath";
import { getNovuLocalization } from "./novuLocalization";

const NOVU_GLOBAL_STYLE_ID = "novu-inbox-overrides";

// Novu (REST + WebSocket) is served same-origin behind the ingress under these
// path prefixes in every environment, and locally via src/setupProxy.js. Deriving
// the origin at runtime keeps the bundle free of any deployment hostname.
//
// The SDK does not accept path-relative values, so origin and path are passed
// separately:
//   - @novu/js strips leading slashes when joining `backendUrl` with the request
//     path, so a bare "/novu/api" would resolve relative to the current page.
//   - socket.io-client treats the path component of its URL as a *namespace*,
//     not as the WebSocket endpoint path — the endpoint path lives in the `path`
//     option (default "/socket.io/"). `socketUrl` must also be non-empty, or Novu
//     falls back to its hosted socket endpoint.
const NOVU_API_PATH = "/novu/api";
const NOVU_WS_PATH = "/novu/ws";

const NOVU_BACKEND_URL = `${window.location.origin}${NOVU_API_PATH}`;
const SOCKET_URL = window.location.origin;
const SOCKET_OPTIONS = { path: `${NOVU_WS_PATH}/socket.io/` };

// Novu's UI applies class names of the form `nv-<elementKey>` (see useStyle()
// in @novu/js/dist/esm/ui/index.mjs). We override a few of those to:
//   - shrink the popover trigger so it matches the surrounding Joy IconButtons
//   - hide the preferences cog (we manage preferences in /profile/notificationSettings/preferences)
//   - hide the "Inbox by Novu" branding link in the inbox footer
//
// We also normalize the surrounding Joy/Material IconButtons inside the shared
// Header so all four icons (FAQ, bell, settings, profile) share the same circular
// hover footprint. The matching tweaks are upstreamed to the registry but ship
// here too as a CSS fallback for the currently-installed package version.
const NOVU_GLOBAL_CSS = `
  .nv-inbox__popoverTrigger {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    padding: 6px !important;
    margin-right: 12px !important;
    color: #333 !important;
    background: transparent !important;
    border-radius: 50% !important;
    box-sizing: border-box !important;
  }
  .nv-inbox__popoverTrigger:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
  .nv-preferences__button { display: none !important; }
  a[href*="go.novu.co/powered"] { display: none !important; }

  /* Hide the entire inbox footer — covers the "Inbox by Novu" link, the
     "Development mode" indicator, and the diagonal-stripes background that
     Novu renders when the session reports isDevelopmentMode=true. The
     footer is the only element inside the popover that combines
     nt-mt-auto + nt-text-foreground-alpha-400. */
  .nv-inbox__popoverContent .nt-mt-auto.nt-text-foreground-alpha-400 {
    display: none !important;
  }

  /* Use the application font (Noto Sans) inside every Novu surface — the
     bell popover plus any portaled dropdowns / snooze pickers. */
  .nv-inbox__popoverContent,
  .nv-inbox__popoverContent *,
  [class*="nv-"][class*="dropdownContent"],
  [class*="nv-"][class*="dropdownContent"] *,
  [class*="nv-"][class*="popoverContent"],
  [class*="nv-"][class*="popoverContent"] * {
    font-family: "Noto Sans", sans-serif !important;
  }

  .header-wrapper .MuiIconButton-root {
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    min-height: 36px !important;
    padding: 6px !important;
    border-radius: 50% !important;
    box-sizing: border-box !important;
  }
  .header-wrapper .MuiIconButton-root:hover {
    background-color: rgba(0, 0, 0, 0.04) !important;
  }
  /* Center every direct icon child of the header's right-side icon row.
     Targets the HeaderIconMenu Box and the ProfileMenu wrapper Box so
     their IconButtons share the same baseline as FAQ/bell/settings. */
  .header-wrapper > div:last-child > div {
    align-items: center !important;
  }

  /* Notification card text — mirror the Key-Value pattern in
     src/components/AssetNameplate/index.tsx (body-sm + Joy fontWeight="lg"). */
  .nv-notificationSubject {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    font-weight: 700 !important;
    color: #333 !important;
  }
  .nv-notificationBody {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    font-weight: 400 !important;
    color: #555 !important;
  }
  .nv-notificationDate {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
    font-weight: 400 !important;
    color: #888 !important;
  }

  /* Top-bar: status selector + more-actions trigger at body-sm */
  .nv-inboxStatus__title,
  .nv-inboxStatus__dropdownTrigger,
  .nv-moreActions__dropdownTrigger {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
  }
  .nv-moreActions__dots {
    width: 16px !important;
    height: 16px !important;
  }

  /* Both top-bar dropdown menus: entries at body-sm */
  .nv-inboxStatus__dropdownItem,
  .nv-inboxStatus__dropdownItemLabel,
  .nv-moreActions__dropdownItem,
  .nv-moreActions__dropdownItemLabel {
    font-size: 0.875rem !important;
    line-height: 1.5 !important;
  }
  .nv-inboxStatus__dropdownItem__icon,
  .nv-inboxStatus__dropdownItemLeft__icon,
  .nv-inboxStatus__dropdownItemRight__icon,
  .nv-moreActions__dropdownItemLeft__icon {
    width: 16px !important;
    height: 16px !important;
  }
`;

const useNovuGlobalStyles = () => {
    useEffect(() => {
        if (document.getElementById(NOVU_GLOBAL_STYLE_ID)) return;
        const el = document.createElement("style");
        el.id = NOVU_GLOBAL_STYLE_ID;
        el.textContent = NOVU_GLOBAL_CSS;
        document.head.appendChild(el);
    }, []);
};

const SETTINGS_BUTTON_CLASS = "novu-custom-settings-button";

// Material Symbols `SettingsRounded` (w300) path + viewBox, taken verbatim
// from @mui-symbols-material/w300/SettingsRounded.js so the injected button
// matches the icon set used elsewhere in the app.
const SETTINGS_ICON_VIEWBOX = "0 -960 960 960";
const SETTINGS_ICON_PATH_D =
    "M435.69-100q-20.46 0-35.34-13.58-14.89-13.57-18.12-33.42l-9.77-74.85q-16.07-5.38-32.96-15.07-16.88-9.7-30.19-20.77L240-228.23q-18.85 8.31-37.88 1.61-19.04-6.69-29.58-24.3l-45.08-78.16q-10.54-17.61-6.07-37.27 4.46-19.65 20.46-32.42l59.92-45q-1.38-8.92-1.96-17.92t-.58-17.93q0-8.53.58-17.34t1.96-19.27l-59.92-45q-16-12.77-20.27-32.62-4.27-19.84 6.27-37.46l44.69-77q10.54-17.23 29.58-24.11 19.03-6.89 37.88 1.42l68.92 29.08q14.47-11.46 30.89-20.96t32.27-15.27L382.23-813q3.23-19.85 18.12-33.42Q415.23-860 435.69-860h88.62q20.46 0 35.34 13.58 14.89 13.57 18.12 33.42l9.77 75.23q18 6.54 32.57 15.27 14.58 8.73 29.43 20.58L720.39-731q18.84-8.31 37.88-1.42 19.04 6.88 29.57 24.11l44.7 77.39q10.54 17.61 6.07 37.27-4.46 19.65-20.46 32.42l-61.46 46.15q2.15 9.69 2.35 18.12.19 8.42.19 16.96 0 8.15-.39 16.58-.38 8.42-2.76 19.27l60.3 45.38q16 12.77 20.66 32.42 4.65 19.66-5.89 37.27l-45.31 77.77q-10.53 17.62-29.76 24.31T718-228.62l-68.46-29.46q-14.85 11.85-30.31 20.96-15.46 9.12-31.69 14.89L577.77-147q-3.23 19.85-18.12 33.42Q544.77-100 524.31-100zm4.31-60h78.62L533-267.15q30.62-8 55.96-22.73 25.35-14.74 48.89-37.89L737.23-286l39.39-68-86.77-65.38q5-15.54 6.8-30.47 1.81-14.92 1.81-30.15 0-15.62-1.81-30.15-1.8-14.54-6.8-29.7L777.38-606 738-674l-100.54 42.38q-20.08-21.46-48.11-37.92-28.04-16.46-56.73-23.31L520-800h-79.38l-13.24 106.77q-30.61 7.23-56.53 22.15-25.93 14.93-49.47 38.46L222-674l-39.38 68L269-541.62q-5 14.24-7 29.62t-2 32.38q0 15.62 2 30.62t6.62 29.62l-86 65.38L222-286l99-42q22.77 23.38 48.69 38.31 25.93 14.92 57.31 22.92zm40.46-200q49.92 0 84.96-35.04T600.46-480t-35.04-84.96T480.46-600q-50.54 0-85.27 35.04T360.46-480t34.73 84.96T480.46-360M480-480";

// Inject a "Settings" icon button into Novu's inbox header, right next to the
// 3-dots more-actions trigger. The library doesn't expose a render prop for
// the top bar, so we observe the document for the trigger rendering, clone
// it to inherit padding/hover styles exactly, swap the icon, and attach our
// navigation handler.
const useInboxHeaderSettingsButton = (label: string, onActivate: () => void) => {
    useEffect(() => {
        const observer = new MutationObserver(() => {
            const triggers = document.querySelectorAll<HTMLElement>(
                ".nv-moreActions__dropdownTrigger"
            );
            triggers.forEach((trigger) => {
                const parent = trigger.parentElement;
                if (!parent) return;
                if (parent.querySelector(`.${SETTINGS_BUTTON_CLASS}`)) return;

                // Clone the more-actions trigger so the new button inherits
                // every Novu layout class (padding, size, hover). cloneNode
                // does NOT copy event listeners, so the popover open handler
                // stays on the original trigger.
                const button = trigger.cloneNode(true) as HTMLElement;
                button.classList.add(SETTINGS_BUTTON_CLASS);
                button.setAttribute("aria-label", label);
                button.setAttribute("title", label);
                button.removeAttribute("aria-controls");
                button.removeAttribute("aria-expanded");
                button.removeAttribute("aria-haspopup");

                const svg = button.querySelector("svg");
                if (svg) {
                    svg.setAttribute("viewBox", SETTINGS_ICON_VIEWBOX);
                    svg.setAttribute("fill", "none");
                    svg.innerHTML = `<path d="${SETTINGS_ICON_PATH_D}" fill="currentColor"/>`;
                }

                const handle = (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onActivate();
                    // Close the inbox popover so it doesn't linger after navigation.
                    document.dispatchEvent(
                        new KeyboardEvent("keydown", { key: "Escape", bubbles: true })
                    );
                };
                button.addEventListener("click", handle);
                button.addEventListener("keydown", (e) => {
                    if ((e as KeyboardEvent).key === "Enter") handle(e);
                });

                parent.insertBefore(button, trigger);
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [label, onActivate]);
};


const NovuNotificationBell = () => {
    const subscriber = useNovuSubscriber();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const { downloadReportFile } = useReportFileDownload();
    useNovuGlobalStyles();
    useInboxHeaderSettingsButton(t("Notification settings"), () =>
        navigate("/profile/notificationSettings/preferences")
    );

    // Novu's notification "unread" dot uses nt-bg-primary, driven by the
    // colorPrimary appearance variable (default Novu purple #7D52F4).
    // Map it to our Joy palette so the dot — and other primary-tinted bits
    // like the "x new notifications" pill — match the app theme.
    const novuAppearance = useMemo<InboxProps["appearance"]>(
        () => ({
            variables: {
                colorPrimary: theme.palette.primary[500],
            },
        }),
        [theme.palette.primary]
    );

    if (!subscriber) return null;

    const localization = getNovuLocalization(i18n.language);

    // Report notification clicks are handled here, in place, without leaving
    // the current view. Novu calls onNotificationClick synchronously and then
    // unconditionally "navigates": relative redirect paths go to routerPush
    // (swallowed below), absolute URLs go to window.open(url, "_blank") —
    // which we suppress for exactly this one URL so no tab to the deployed
    // host opens. Direct opens of the URL (e.g. mail links) are covered by
    // the /installedBase/:nodeId/files/:fileId route.
    const handleNotificationClick = (notification: { redirect?: { url?: string } | null }) => {
        const redirectUrl = notification.redirect?.url;
        const target = parseInstalledBaseFileNotificationPath(redirectUrl);
        if (!target || !redirectUrl) return;

        const originalOpen = window.open;
        const patchedOpen: typeof window.open = (url?: string | URL, ...rest) => {
            window.open = originalOpen;
            if (String(url) === redirectUrl) return null;
            return originalOpen(url, ...rest);
        };
        window.open = patchedOpen;
        // Defensive restore in case Novu never issues the window.open call
        // (relative redirect paths go through routerPush instead).
        setTimeout(() => {
            if (window.open === patchedOpen) window.open = originalOpen;
        }, 0);

        // Close the inbox popover (same trick as the injected settings
        // button) — the user stays on the current view.
        document.dispatchEvent(
            new KeyboardEvent("keydown", { key: "Escape", bubbles: true })
        );
        void downloadReportFile(target);
    };

    return (
        <Inbox
            applicationIdentifier={subscriber.applicationId}
            subscriber={subscriber.subscriberId}
            subscriberHash={subscriber.hmacHash}
            backendUrl={NOVU_BACKEND_URL}
            socketUrl={SOCKET_URL}
            socketOptions={SOCKET_OPTIONS}
            appearance={novuAppearance}
            localization={localization}
            onNotificationClick={handleNotificationClick}
            // Hand notification clicks back to React Router so SPA navigation
            // works (Novu's default falls back to history.pushState which
            // updates the URL but doesn't trigger React Router re-render).
            // Report file targets were already downloaded in
            // onNotificationClick — just block the navigation here.
            routerPush={(path) => {
                if (parseInstalledBaseFileNotificationPath(path)) return;
                navigate(path);
            }}
            renderBell={(unreadCount) => (
                <Box sx={{ display: "inline-flex", alignItems: "center", color: "#333" }}>
                    <Badge badgeContent={unreadCount.total} color="primary">
                        <NotificationsFilledRounded />
                    </Badge>
                </Box>
            )}
        />
    );
};

export default NovuNotificationBell;
